import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { Chart } from 'klinecharts'
import { precisionFromPointSize } from '../../bot/adapters/symbolsFetcher'
import { formatDateLabel } from '../../utils/chartUtils'
import { registerBacktestChartOverlays } from '../../bot/components/backtestChartOverlays'
import { OrderLayer, type OrderMarkerClick } from '../../bot/components/replayOrderLayer'
import type { BotCode } from '../../bot/api'
import type { OrderData } from '../../bot/types/chartData'
import type { FormingBarState } from '../../bot/utils/formingBarBuffer'
import { WorkbenchReplayBar } from './WorkbenchReplayBar'
import WalkthroughTradesPane from './WalkthroughTradesPane'
import useWalkthroughOrders from './useWalkthroughOrders'
import useCanvasReplay from './useCanvasReplay'
import { useReplayDrawingVisibility } from './replayDrawingVisibility'
import { hideWalkthroughSections, sectionColors, showWalkthroughSections } from './walkthroughSections'
import { useIsDark } from './useIsDark'
import { clampBarMs, DEFAULT_BAR_MS } from './walkthroughPacing'
import type { CanvasSeries } from './canvasSeries'
import type { WalkthroughRun } from './useWalkthroughOverlay'

const DEFAULT_PRECISION = 5

/** A trade arrow on the canvas was clicked, with the run's numbering for its card. */
export interface WalkthroughMarkerClick extends OrderMarkerClick {
  tickets: ReadonlyMap<number, number>
  precision: number
}

export interface WorkbenchWalkthroughProps {
  run: WalkthroughRun
  /** The canvas chart, and the series that owns its candles. */
  chart: Chart | null
  series: CanvasSeries | null
  /** The canvas's live symbol and timeframe. */
  symbol: string
  timeframe: string
  /** The walkthrough is on the chart (the toolbar's Walkthrough toggle). */
  shown: boolean
  /** The user's drawings, by overlay id, and a counter bumped on every edit. */
  drawIdsRef: React.RefObject<Set<string>>
  drawingsVersion: number
  /** A drawing not to hide right now: the one being dragged or drawn. */
  isDrawingExempt: (id: string) => boolean
  /** Some drawings went out of sight behind the playhead. */
  onDrawingsHidden: (ids: string[]) => void
  /** True while the user presses or drags on the chart. */
  holdViewport: () => boolean
  /** Whether the replay is cutting the canvas right now. */
  onCutoffChange: (active: boolean) => void
  onMarkerClick: (click: WalkthroughMarkerClick) => void
  /** Where the design tokens can be read from (any element under `.v1`). */
  tokenHost: React.RefObject<HTMLElement | null>
  /** The bot's current code, as the Backtest tab shows it — for the pane's Code tab. */
  currentCode?: BotCode | null
  hasCode?: boolean
}

/**
 * The walkthrough on the canvas: the generated code trading the period the user drew on, played
 * back on the same chart they draw on.
 *
 * Nothing here owns a chart. The candles are the canvas's own (CanvasSeries), cut at the playhead
 * while the replay runs (useCanvasReplay); the run's trades are drawn onto the canvas by the same
 * order layer the Backtest tab uses; the run's time sections are tinted behind the candles; and
 * the user's drawings past the playhead are hidden until it reaches them. What this renders is
 * the band — the transport — and the trades pane under it.
 *
 * Hidden (the toolbar's Walkthrough toggle), every one of those layers comes off the chart, the
 * cutoff lifts and nothing renders here: the canvas is the canvas again. The hooks keep running,
 * so the replay keeps its place for when it is shown again.
 */
export default function WorkbenchWalkthrough({
  run,
  chart,
  series,
  symbol,
  timeframe,
  shown,
  drawIdsRef,
  drawingsVersion,
  isDrawingExempt,
  onDrawingsHidden,
  holdViewport,
  onCutoffChange,
  onMarkerClick,
  tokenHost,
  currentCode = null,
  hasCode = false,
}: WorkbenchWalkthroughProps) {
  const isDark = useIsDark()
  const onChart = shown && symbol === run.symbol

  // ---- Pacing: one value, wall-clock ms per bar, the same on every timeframe -------------
  const [barMs, setBarMs] = useState(DEFAULT_BAR_MS)
  const handleBarMsChange = useCallback((next: number) => setBarMs(clampBarMs(next)), [])

  const bc = run.configInput?.backtest_config ?? {}
  const pipSize = typeof bc.pip_size === 'number' ? bc.pip_size : 0.0001
  const precision =
    precisionFromPointSize(typeof bc.point_size === 'number' ? bc.point_size : null) ?? DEFAULT_PRECISION

  // The run's own trades, fetched once and numbered once — the pane's rows and the markers' card
  // have to agree on which trades exist and what each one is called.
  const { orders: runOrders, tickets } = useWalkthroughOrders({
    btid: run.btid,
    rangeStartTs: run.rangeStartTs,
    rangeEndTs: run.rangeEndTs,
  })

  // ---- The trade overlay ---------------------------------------------------------------
  const layerRef = useRef<OrderLayer | null>(null)
  const latest = useRef({ onMarkerClick, tickets, precision })
  useLayoutEffect(() => {
    latest.current = { onMarkerClick, tickets, precision }
  })
  useLayoutEffect(() => {
    if (!chart) return undefined
    registerBacktestChartOverlays()
    const layer = new OrderLayer(chart, {
      onMarkerClick: (click) =>
        latest.current.onMarkerClick({ ...click, tickets: latest.current.tickets, precision: latest.current.precision }),
    })
    layerRef.current = layer
    return () => {
      layer.clear()
      layerRef.current = null
    }
  }, [chart])

  /** Draw the trades the chart can show, as of `revealTs` (the sub-bar cursor) or the cutoff. */
  const reconcileRef = useRef<(revealTs?: number, forming?: FormingBarState | null) => void>(() => {})

  const replay = useCanvasReplay({
    btid: run.btid,
    series,
    runSymbol: run.symbol,
    symbol,
    timeframe,
    // The replay covers the drawings window only — the inner section. It starts where the
    // window does and is complete where it ends: the cutoff lifts there and the trail and every
    // later candle come back, the last candle played staying where it is.
    startTs: run.displayStartTs,
    endTs: run.displayEndTs,
    // A run opened to be played starts from the top: a cursor parked on an earlier visit would
    // skip the very idea the verification just checked.
    initialProgressView: run.autoplay ? null : run.progressView,
    // A RUN THE USER JUST WAITED FOR STARTS ITSELF — one this session built (`reused` false) and
    // nobody has watched, or one the Auto-verify loop just accepted (`autoplay`). Attaching to a
    // run found on load is the opposite intent: the user parked that cursor themselves.
    autoPlayOnReady: run.autoplay || (!run.reused && run.progressView == null),
    shown: onChart,
    barMs,
    holdViewport,
    onFormingReveal: (revealTs, forming) => reconcileRef.current(revealTs, forming),
  })
  const { active: cutting, cursorTs, version } = replay

  const displayedBars = useMemo(() => {
    if (!series) return []
    const n = series.getAppliedCount()
    const all = series.getCandles()
    return n >= all.length ? all : all.slice(0, n)
    // `version` is the series' change counter: it is what makes this recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, version])

  useLayoutEffect(() => {
    reconcileRef.current = (revealTs, forming = null) => {
      const layer = layerRef.current
      if (!layer) return
      if (!onChart) {
        layer.clear()
        return
      }
      // While cut, only what the playhead has passed — anything later would snap onto the last
      // bar, and would tell the outcome early. Once lifted, the whole run.
      const boundary = cutting ? (revealTs ?? cursorTs) : Infinity
      const orders: OrderData[] =
        boundary === Infinity ? runOrders : runOrders.filter((o) => o.time <= boundary)
      layer.reconcile({
        bars: displayedBars,
        orders,
        timeframe,
        forming,
        revealTs: revealTs ?? Infinity,
        liveOrderLines: true,
        pipSize,
        precision,
      })
    }
  })
  useEffect(() => {
    reconcileRef.current()
  }, [onChart, cutting, cursorTs, runOrders, displayedBars, timeframe, pipSize, precision])

  // ---- The run's time sections, tinted behind the candles ---------------------------------
  useEffect(() => {
    if (!chart) return undefined
    if (!onChart) {
      hideWalkthroughSections(chart)
      return undefined
    }
    showWalkthroughSections(chart, {
      rangeStartMs: run.rangeStartTs * 1000,
      rangeEndMs: run.rangeEndTs * 1000,
      displayStartMs: run.displayStartTs * 1000,
      displayEndMs: run.displayEndTs * 1000,
      ...sectionColors(tokenHost.current),
    })
    return undefined
  }, [chart, onChart, run.rangeStartTs, run.rangeEndTs, run.displayStartTs, run.displayEndTs, isDark, tokenHost])
  useLayoutEffect(() => () => {
    if (chart) hideWalkthroughSections(chart)
  }, [chart])

  // ---- The user's drawings past the playhead ---------------------------------------------
  const lastAppliedMs = cutting && replay.lastAppliedTs != null ? replay.lastAppliedTs * 1000 : null
  useReplayDrawingVisibility({
    chart,
    drawIdsRef,
    lastRevealedMs: lastAppliedMs,
    version: drawingsVersion,
    isExempt: isDrawingExempt,
    onHide: onDrawingsHidden,
  })

  const onCutoffChangeRef = useRef(onCutoffChange)
  useLayoutEffect(() => {
    onCutoffChangeRef.current = onCutoffChange
  })
  useLayoutEffect(() => {
    onCutoffChangeRef.current(cutting)
  }, [cutting])
  useLayoutEffect(() => () => onCutoffChangeRef.current(false), [])

  // ---- The band's terms ------------------------------------------------------------------
  // Where the replay starts, and how many of the run's trades it has revealed — counted on entry
  // events exactly as the trades pane counts its rows. Like the progress bar, the count covers
  // the window the replay plays: the trail's trades show on the chart and in the pane once it is
  // complete, but they are not a target the replay counts towards.
  const openedEvents = useMemo(
    () => runOrders.filter((o) => o.eventType === 'opened' && o.time <= run.displayEndTs),
    [runOrders, run.displayEndTs],
  )
  // Once complete (or lifted), the whole run: the pane and the markers hold every trade.
  const playheadTs = cutting ? cursorTs : run.rangeEndTs
  const tradesRevealed = useMemo(
    () => openedEvents.filter((o) => o.time <= playheadTs).length,
    [openedEvents, playheadTs],
  )

  // Another market on the canvas: there is no walkthrough HERE. Everything above has already
  // stood down (`onChart` is false — no cutoff, no trades, no sections); only the replay's own
  // state stays, for when the user comes back.
  if (symbol !== run.symbol) return null
  // Hidden: the same, by the user's choice.
  if (!shown) return null

  return (
    <>
      <WorkbenchReplayBar
        playing={replay.isPlaying}
        atEnd={replay.atEnd}
        // Play again from the top: a reset lands on the replay start and queues its own play.
        onReplay={replay.handleReset}
        tradesRevealed={tradesRevealed}
        tradeTotal={openedEvents.length}
        fromLabel={formatDateLabel(run.displayStartTs)}
        barMs={barMs}
        onBarMsChange={handleBarMsChange}
        value={replay.scrub.visibleCount}
        max={replay.scrub.totalCandles}
        maxScrubIndex={replay.scrub.maxScrubIndex}
        onScrub={replay.scrub.handleScrub}
        onTogglePlay={replay.handleTogglePlay}
        onStepBack={() => replay.handleStep(-1)}
        onStepForward={() => replay.handleStep(1)}
      />
      <WalkthroughTradesPane
        orders={runOrders}
        playheadTs={playheadTs}
        markPrice={replay.markPrice}
        pipSize={pipSize}
        precision={precision}
        // Scrolls the chart to the trade and leaves the playhead alone.
        onShowTrade={(ts) => void replay.handleShowTime(ts)}
        // The run is over as far as the user is concerned. Lands the pane on History rather
        // than on an empty Open tab.
        atEnd={replay.atEnd}
        currentCode={currentCode}
        hasCode={hasCode}
      />
    </>
  )
}
